/** What the dashboard bands draw, as plain data - ADR-0212, package P9.
 *
 * **Nothing here reads, sums or invents.** Every function takes what a page already read from the
 * server and shapes it for a chart. Three rules govern all of it, each with a test in
 * `bandModel.test.ts`:
 *
 * - **A chart's numbers are geometry, the figures are the server's strings.** `Number` places a
 *   mark; what a reader reads is formatted from the server's decimal string by `lib/format`
 *   (rule 5). A total or a share is never added up here.
 * - **An incomplete month is never a drop.** A month that has not ended, or that the census says has
 *   a bill missing, is `complete: false` with the census's words in `missing`, so the chart draws it
 *   pale and dashed and its tooltip says so. A month nobody has recorded is `null` ("no figures"),
 *   never `0`; a month that has not happened yet is `future`.
 * - **Delta only from a prior figure the server holds, and never over an incomplete window.** A
 *   total that is still arriving compared with a finished year is a fall that did not happen.
 */

import type { InventoryMonth, InventoryMonthly } from "../../api/queries";
import type { Coverage } from "../../api/sources";
import type { Trends } from "../../api/dashboardReads";
import type { BarMonth, SegmentNote } from "../../components/charts";
import type { TileDelta } from "../../components/ui";
import { differenceOf, sumDecimals } from "../../lib/decimal";
import { restsOnEstimates } from "../../lib/estimate";
import { monthLabel, share, shortMonthYear, tonnes } from "../../lib/format";
import { scopeOfCategory } from "../scopeTable/model";

export type ScopeKey = "1" | "2" | "3";

/** A month's kilograms printed as tonnes, **rounded once from the server's exact string** (rule 5,
 * G-P7-01). Two decimals under 10 t, one above: the precision `tonnes` gives a small month, so a
 * tooltip never rounds a real month to zero. This used to take the `Number` the bars are placed
 * with, `toFixed` it to three or four places and round that again, so 511.349913 t printed as
 * 511.4 where 511.3 is the figure. The count of places is chosen from the size (geometry); the
 * figure itself is never a float. */
export function printedTonnes(kg: string): string {
  const places = Math.abs(Number(kg)) / 1000 < 10 ? 2 : 1;
  return tonnes(kg, places);
}

/** Tonnes as a number, for placing a mark only. */
export function geometryTonnes(kg: string): number {
  const value = Number(kg) / 1000;
  return Number.isFinite(value) ? value : 0;
}

// ------------------------------------------------------------------------------------ the census

/** What the census says about the bills of a month: how many are missing, and how many were expected
 * (or already supplied) in all. */
export interface Gap {
  missing: number;
  /** The windows that are due: supplied, declared nil, missing or not known. A window not yet due and
   * one that is not expected are not part of what could be missing. */
  expected: number;
}

const DUE: ReadonlySet<string> = new Set(["submitted", "declared_nil", "missing", "unknown"]);

/** Per calendar month (`YYYY-MM`), the census's gap over the sources that belong to `scope` (or every
 * source, for none). A window is counted in every month it covers, so a missing quarterly bill marks
 * its three months. Only `missing` counts as missing: a window not yet due is the month not having
 * ended, and a declared nil is a statement and not a gap.
 *
 * **This is what makes "still arriving" a per-scope fact.** The chart asks it once for each scope it
 * stacks, so a standby generator's missing bills mark Scope 1's segment and leave Scope 2's alone. */
export function gapsByMonth(coverage: Coverage, scope?: ScopeKey): Map<string, Gap> {
  const found = new Map<string, Gap>();
  for (const source of coverage.sources) {
    if (scope && scopeOfCategory(source.category) !== scope) continue;
    for (const window of source.windows) {
      if (!DUE.has(window.state)) continue;
      const first = Number(window.period_start.slice(0, 4)) * 12 + Number(window.period_start.slice(5, 7)) - 1;
      const last = Number(window.period_end.slice(0, 4)) * 12 + Number(window.period_end.slice(5, 7)) - 1;
      for (let at = first; at <= last; at += 1) {
        const key = `${String(Math.floor(at / 12))}-${String((at % 12) + 1).padStart(2, "0")}`;
        const now = found.get(key) ?? { missing: 0, expected: 0 };
        found.set(key, {
          missing: now.missing + (window.state === "missing" ? 1 : 0),
          expected: now.expected + 1,
        });
      }
    }
  }
  return found;
}

/** How many bills the census says are missing in each calendar month, as plain counts. */
export function missingByMonth(coverage: Coverage, scope?: ScopeKey): Map<string, number> {
  const found = new Map<string, number>();
  for (const [key, gap] of gapsByMonth(coverage, scope)) {
    if (gap.missing > 0) found.set(key, gap.missing);
  }
  return found;
}

/** `2 bills missing`, the census's words for a month. */
export function missingWords(count: number): string {
  return `${String(count)} bill${count === 1 ? "" : "s"} missing`;
}

// ------------------------------------------------------------------------------------- the bars

export interface MonthStatus {
  complete: boolean;
  future: boolean;
  /** What is outstanding, for the tooltip, or undefined for a complete month. */
  missing?: string;
}

/** Whether a month is whole. Unknown census (`undefined`) is treated as "not known to be whole":
 * the caller does not draw bars at all until the census has answered, so this is only reached with
 * one. */
export function statusOf(month: InventoryMonth, missing: ReadonlyMap<string, number>): MonthStatus {
  const gaps = missing.get(month.month) ?? 0;
  const future = !month.has_ended && month.state === "no_records";
  if (future) return { complete: true, future: true };
  if (gaps > 0) return { complete: false, future: false, missing: missingWords(gaps) };
  if (!month.has_ended) return { complete: false, future: false, missing: "month not over" };
  return { complete: true, future: false };
}

/** More than a tenth of a scope's expected bills missing in a month and its segment is drawn pale.
 * At or under it the segment stays solid and carries the small cap, so one missing bill among two
 * hundred is told and does not turn a stack into a ghost. Compared in whole numbers. */
export function isMostlyMissing(gap: Gap): boolean {
  return gap.missing * 10 > gap.expected;
}

/** `1 of 214 bills missing`, the census's words for one scope's segment of a month. */
export function gapWords(gap: Gap): string {
  return `${String(gap.missing)} of ${String(gap.expected)} bill${gap.expected === 1 ? "" : "s"} missing`;
}

/** The gap per scope, by month. A scope the census has no source for has no entry. */
export type ScopeGaps = Readonly<Partial<Record<ScopeKey, ReadonlyMap<string, Gap>>>>;

/** The census asked once for each scope, for the scopes a chart stacks. */
export function scopeGaps(coverage: Coverage, scopes: readonly ScopeKey[]): ScopeGaps {
  const found: Partial<Record<ScopeKey, ReadonlyMap<string, Gap>>> = {};
  for (const scope of scopes) found[scope] = gapsByMonth(coverage, scope);
  return found;
}

/** The share of the month's total that rests on estimates, in the server's tonnes, or undefined when
 * nothing does or when the bars do not hold every scope the month assessed (a single scope's page). */
function estimateLine(month: InventoryMonth, scopes: readonly ScopeKey[]): string | undefined {
  if (!month.has_estimate || month.state !== "recorded") return undefined;
  const holdsEvery = month.by_scope.every(
    (cell) => !cell.assessed || scopes.includes(cell.scope as ScopeKey),
  );
  if (!holdsEvery) return undefined;
  const part = share(month.estimated_kgco2e, month.kgco2e);
  return `Estimates: ${tonnes(month.estimated_kgco2e)} t${part ? ` (${part})` : ""}`;
}

/** The monthly bars' months for the scopes asked, stacked in `scopes` order. A scope the server did
 * not assess in a month is `null` there (no figure), and a month with no records at all has every
 * value `null`. `receipt` is the month key, which the page's handler turns into the month's figures.
 *
 * **Incompleteness is judged per scope.** A month that has not ended is incomplete for every scope.
 * Otherwise each scope's segment is judged by the census over that scope's own sources: any missing
 * bill puts a note on that segment, and it is drawn pale only when more than a tenth of what was
 * expected is missing. So Scope 2's segment is whole on Home exactly when it is whole on Scope 2's
 * own page, which is the contradiction this replaces (it was judged once for the month, over every
 * source, so a generator's missing bills paled the electricity beside it). */
export function barMonths(
  months: readonly InventoryMonth[],
  scopes: readonly ScopeKey[],
  gaps: ScopeGaps,
): BarMonth[] {
  return months.map((month) => {
    const future = !month.has_ended && month.state === "no_records";
    const open = !future && !month.has_ended;
    const values: Record<string, number | null> = {};
    const printed: Record<string, string> = {};
    const notes: Record<string, SegmentNote> = {};
    for (const scope of scopes) {
      const cell = month.by_scope.find((candidate) => candidate.scope === scope);
      values[scope] = month.state === "recorded" && cell?.assessed ? geometryTonnes(cell.kgco2e) : null;
      if (month.state === "recorded" && cell?.assessed) printed[scope] = printedTonnes(cell.kgco2e);
      const gap = gaps[scope]?.get(month.month);
      if (gap && gap.missing > 0 && !future) {
        notes[scope] = {
          text: gapWords(gap),
          ...(isMostlyMissing(gap) ? { pale: true } : {}),
        };
      }
    }
    const detail = estimateLine(month, scopes);
    return {
      key: month.month,
      label: shortMonthYear(month.month).split(" ")[0] ?? month.month,
      full: monthLabel(month.month),
      values,
      printed,
      ...(future ? { future: true } : {}),
      ...(open ? { complete: false, missing: "month not over" } : {}),
      ...(Object.keys(notes).length > 0 ? { notes } : {}),
      ...(month.state === "recorded"
        ? { total: geometryTonnes(month.kgco2e), totalPrinted: printedTonnes(month.kgco2e) }
        : {}),
      ...(detail ? { detail } : {}),
      receipt: month.month,
    };
  });
}

/** The window's estimate sentence, in tonnes: how much of the shown total rests on estimates (grade C
 * or D). Both figures are the server's, summed exactly; the share is `lib/format`'s division. Absent
 * when nothing rests on estimates or when the total cannot be read. */
export function estimateNote(series: InventoryMonthly): string | undefined {
  const estimated = sumDecimals(series.months.map((month) => month.estimated_kgco2e));
  if (estimated === undefined || !(Number(estimated) > 0)) return undefined;
  const part = share(estimated, series.kgco2e);
  // Says which reading it counts in, from the series itself (`lib/estimate`): the Estimates tile
  // counts every figure below grade A, this counts what the server says it counts.
  return `\u2248 ${tonnes(estimated)} t of ${tonnes(series.kgco2e)} t${part ? ` (${part})` : ""} rests on ${restsOnEstimates(series.estimate_reading)}`;
}

/** The tile's small line: tonnes of each month that is recorded **and whole**, in order. An
 * incomplete month is left out rather than drawn as a dip, so the line stops where the data does. */
export function sparkOf(
  months: readonly InventoryMonth[],
  scope: ScopeKey | undefined,
  missing: ReadonlyMap<string, number>,
): number[] {
  const series: number[] = [];
  for (const month of months) {
    if (month.state !== "recorded") continue;
    const status = statusOf(month, missing);
    if (!status.complete || status.future) continue;
    if (scope === undefined) {
      series.push(geometryTonnes(month.kgco2e));
      continue;
    }
    const cell = month.by_scope.find((candidate) => candidate.scope === scope);
    if (cell?.assessed) series.push(geometryTonnes(cell.kgco2e));
  }
  return series;
}

/** Whether every month of the window has ended and the census has no bill missing in it. */
export function windowIsWhole(
  months: readonly InventoryMonth[],
  missing: ReadonlyMap<string, number>,
): boolean {
  return months.length > 0 && months.every((month) => statusOf(month, missing).complete);
}

// ------------------------------------------------------------------------------------- deltas

const unsigned = (value: string): string => value.replace(/^[-+]/, "");

function deltaOfKg(delta: string, note?: string): TileDelta {
  const magnitude = Math.abs(Number(delta));
  const direction = magnitude === 0 ? "flat" : Number(delta) > 0 ? "up" : "down";
  return {
    value: `${tonnes(unsigned(delta))} t`,
    direction,
    tone: "neutral",
    against: "vs prior period",
    ...(note ? { note } : {}),
  };
}

/** **How complete the period a change is against is** (G-Wviewer-07). A part-year prior (the seeded
 * FY2024 holds a few months of a company's activity) makes a whole current year read as a tripling.
 * The server's `prior_months` is the prior window month by month; a month with `no_records` is one
 * the prior period has nothing in. The note is made only when some are missing, and says how many of
 * how many were recorded; a prior the server did not break down (`null`) has no note, because the
 * product cannot say it is partial and must not say it is whole. */
export function priorNote(trends: Trends): string | undefined {
  const months = trends.prior_months;
  if (!months || months.length === 0) return undefined;
  const recorded = months.filter((month) => month.state === "recorded").length;
  if (recorded === months.length) return undefined;
  return `Prior period: ${String(recorded)} of ${String(months.length)} months recorded`;
}

/** A scope's change against the prior window: the server's own `delta_kgco2e`, and only when the
 * server holds a prior figure for it and the current window is whole. */
export function scopeDelta(trends: Trends | undefined, scope: ScopeKey, whole: boolean): TileDelta | undefined {
  if (!trends || !whole) return undefined;
  const row = trends.scopes.find((candidate) => candidate.scope === scope);
  if (!row || row.prior_figures === 0 || row.prior_kgco2e === null || row.delta_kgco2e === null) return undefined;
  return deltaOfKg(row.delta_kgco2e, priorNote(trends));
}

/** The whole inventory's change against the prior window, from the two totals the server states for
 * the same method (`kgco2e` and the comparative's calculated prior), subtracted exactly. Only when
 * the server calculated a prior and the current window is whole. */
export function totalDelta(trends: Trends | undefined, whole: boolean): TileDelta | undefined {
  if (!trends || !whole) return undefined;
  const { comparative } = trends;
  if (comparative.basis !== "calculated" || comparative.state !== "available") return undefined;
  if (comparative.calculated_kgco2e === null) return undefined;
  const difference = differenceOf(trends.kgco2e, comparative.calculated_kgco2e);
  return difference === undefined ? undefined : deltaOfKg(difference, priorNote(trends));
}
